// The Generate tab of Create (DEVELOPMENT.md: Generator → Generate button): a size, regions, any
// rules and a difficulty; a pool puzzle that is exactly that opens at once, otherwise the worker
// makes one while a progress bar runs. Uses the Settings segmented control and the library's
// chips and menu.
import {
  BANDS,
  GENERATE_GLOBALS,
  GENERATE_RULES,
  GENERATE_SIZES,
  aGrid,
  alwaysIrregular,
  bandsFor,
  fitChoice,
  maxRules,
  neverFog,
  neverIrregular,
  normalizeRequest,
  poolMatches,
  type BandKey,
  type GenerateJobStatus,
  type GenerateOptions,
  type GenerateRequest,
  type GenerateResponse,
} from '@zinc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { ChipMenu, ToggleChip, chipClass } from './library/ChipMenu';
import { Card, Segmented } from './ui';

const LAST = 'zinc.generate';
const FIRST: GenerateRequest = {
  size: 9,
  irregular: false,
  rules: [],
  globals: [],
  surprise: false,
  count: null,
  fog: false,
  band: 'medium',
};

/** The last choice on this device: a convenience, so anything wrong with it is ignored. */
function lastChoice(): GenerateRequest {
  try {
    const saved = normalizeRequest(
      JSON.parse(localStorage.getItem(LAST) ?? 'null') as GenerateRequest,
    );
    return typeof saved === 'string' ? FIRST : saved;
  } catch {
    return FIRST;
  }
}

function remember(choice: GenerateRequest): void {
  try {
    localStorage.setItem(LAST, JSON.stringify(choice));
  } catch {
    // Private window or storage off: the picker just starts from its defaults next time.
  }
}

const COMMON = GENERATE_RULES.filter((r) => r.common);
const MORE = GENERATE_RULES.filter((r) => !r.common);
const MORE_OPTIONS = [
  ...MORE.map((r) => ({ value: r.key, label: r.name })),
  ...GENERATE_GLOBALS.map((g, i) => ({
    value: g.key,
    label: g.name,
    ...(i === 0 ? { group: 'Whole grid' } : {}),
  })),
];
const isGlobal = (key: string) => GENERATE_GLOBALS.some((g) => g.key === key);
const nameOf = (key: string) =>
  [...GENERATE_RULES, ...GENERATE_GLOBALS].find((r) => r.key === key)?.name ?? key;

/** "a 6×6 killer and kropki puzzle", "a 9×9 classic puzzle", "an 8×8 puzzle with 5 rules",
 * "a 9×9 fog puzzle", "a 6×6 fog puzzle with killer". */
function describe(c: GenerateRequest): string {
  const grid = aGrid(c.size);
  if (c.surprise && c.count !== null) {
    const rules = c.count === 1 ? '1 rule' : `${c.count} rules`;
    return `${grid} ${c.fog ? 'fog ' : ''}puzzle with ${rules}`;
  }
  if (c.surprise) return `${grid} ${c.fog ? 'fog' : 'mixed'} puzzle`;
  const all = [...c.rules, ...c.globals];
  if (c.fog) {
    if (all.length > 3) return `${grid} fog puzzle with ${all.length} rules`;
    const names = all.map((k) => nameOf(k).toLowerCase());
    return `${grid} fog puzzle with ${names.join(names.length > 2 ? ', ' : ' and ')}`;
  }
  if (all.length === 0) return `${grid} ${c.irregular ? 'irregular' : 'classic'} puzzle`;
  if (all.length > 3) return `${grid} puzzle with ${all.length} rules`;
  const names = all.map((k) => nameOf(k).toLowerCase());
  return `${grid} ${names.join(names.length > 2 ? ', ' : ' and ')} puzzle`;
}

const capitalized = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** What Surprise me will do with what's chosen. */
function surpriseWords(c: GenerateRequest): string {
  const inFog = c.fog ? ', in fog' : '';
  const more = (n: number) => (n === 1 ? '1 rule' : `${n} rules`);
  if (c.rules.length === 0) {
    return c.count === null
      ? `The generator picks a mix of rules${inFog}.`
      : `The generator picks ${more(c.count)}${inFog}, every one in the puzzle.`;
  }
  const names = c.rules.map((k) => nameOf(k).toLowerCase());
  const chosen = capitalized(names.join(names.length > 2 ? ', ' : ' and '));
  if (c.count === null) return `${chosen}, plus a mix the generator picks${inFog}.`;
  const extra = c.count - c.rules.length;
  return extra === 0
    ? `${chosen}, and no others${inFog}.`
    : `${chosen}, plus ${more(extra)} the generator picks${inFog}, every one in the puzzle.`;
}

const waitFor = (size: number, fog: boolean) =>
  size >= 16
    ? 'Takes a minute or two.'
    : size >= 12 || (fog && size >= 8)
      ? 'Takes up to a minute.'
      : 'Takes a few seconds.';

export function GeneratePanel() {
  const navigate = useNavigate();
  const client = useQueryClient();
  const options = useQuery({
    queryKey: ['generate', 'options'],
    queryFn: () => api<GenerateOptions>('/api/generate/options'),
  });
  // What was asked for, and that fitted to the grid: a limit doesn't overwrite the wish, so a
  // difficulty or regions come back when the choice allows them again.
  const [wanted, setWanted] = useState<GenerateRequest>(lastChoice);
  const choice = fitChoice(wanted);
  const [jobId, setJobId] = useState<string | null>(null);
  const { size, irregular, rules, globals, surprise, count, fog, band } = choice;
  const reachable = bandsFor(choice);
  const ready = (options.data?.pool ?? []).some((p) => poolMatches(choice, p));
  const plain = !surprise && rules.length + globals.length === 0;

  const toggleRule = (key: string) =>
    setWanted({
      ...wanted,
      rules: rules.includes(key) ? rules.filter((r) => r !== key) : [...rules, key],
    });

  const open = (puzzleId: string) => {
    void client.invalidateQueries({ queryKey: ['library'] });
    void navigate(`/puzzle/${puzzleId}`);
  };

  const start = useMutation({
    mutationFn: () => {
      remember(choice);
      return api<GenerateResponse>('/api/generate', { method: 'POST', json: choice });
    },
    onSuccess: (r) => ('puzzleId' in r ? open(r.puzzleId) : setJobId(r.jobId)),
  });

  const job = useQuery({
    queryKey: ['generate', 'job', jobId],
    queryFn: () => api<GenerateJobStatus>(`/api/generate/jobs/${jobId}`),
    enabled: jobId !== null,
    refetchInterval: (q) =>
      q.state.data?.state === 'done' || q.state.data?.state === 'failed' ? false : 1000,
  });
  useEffect(() => {
    if (job.data?.state === 'done' && job.data.puzzleId) open(job.data.puzzleId);
    // open() only navigates; running it once per finished job is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data?.state, job.data?.puzzleId]);

  const failed = start.isError || job.data?.state === 'failed';
  const hardest = BANDS.find((b) => b.key === reachable[reachable.length - 1])?.name;
  const grid = `${size}×${size}`;
  const whichGrid = fog
    ? 'Fog'
    : !plain
      ? `${capitalized(aGrid(size))} with rules`
      : irregular
        ? `An irregular ${grid}`
        : `A classic ${grid}`;

  return (
    <section aria-label="Generate">
      <Card className="flex flex-col gap-3 px-4 py-4">
        {jobId ? (
          <>
            <p className="m-0 text-[15px]">
              Making {describe(choice)}, {BANDS.find((b) => b.key === band)?.name.toLowerCase()}…
            </p>
            <div
              role="progressbar"
              aria-label="Making the puzzle"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round((job.data?.progress ?? 0) * 100)}
              className="h-2 overflow-hidden rounded-full bg-segment"
            >
              <span
                className="block h-full rounded-full bg-accent transition-[width] duration-700"
                style={{ width: `${Math.max(4, (job.data?.progress ?? 0) * 100)}%` }}
              />
            </div>
            {failed ? (
              <p role="alert" className="m-0 text-[14px] text-mistake">
                {job.data?.error ?? 'That didn’t work. Try again.'}
              </p>
            ) : (
              <p className="m-0 text-[13px] text-muted">
                {waitFor(size, fog)} You can leave this page: the puzzle will be in the Library.
              </p>
            )}
            <button
              type="button"
              onClick={() => setJobId(null)}
              className="h-12 rounded-[12px] border border-line bg-surface text-[15px] font-semibold text-ink"
            >
              Back
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-muted">Size</span>
              <div role="radiogroup" aria-label="Size" className="flex flex-wrap gap-1.5">
                {GENERATE_SIZES.map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={n === size}
                    onClick={() => setWanted({ ...wanted, size: n })}
                    className={chipClass(n === size)}
                  >
                    {n}×{n}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-muted">Regions</span>
              <Segmented
                label="Regions"
                value={irregular ? 'irregular' : 'boxes'}
                onChange={(v) => setWanted({ ...wanted, irregular: v === 'irregular' })}
                options={[
                  { value: 'boxes', label: 'Boxes', disabled: alwaysIrregular(size) },
                  { value: 'irregular', label: 'Irregular', disabled: neverIrregular(size) },
                ]}
              />
              {alwaysIrregular(size) && (
                <p className="mx-1 my-0 text-[12px] text-muted">
                  {capitalized(aGrid(size))} has no rectangular boxes.
                </p>
              )}
              {neverIrregular(size) && (
                <p className="mx-1 my-0 text-[12px] text-muted">
                  Irregular {grid} grids take too long to make.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-muted">Rules</span>
              <div role="group" aria-label="Rules" className="flex flex-wrap gap-1.5">
                {COMMON.map((r) => (
                  <ToggleChip
                    key={r.key}
                    label={r.name}
                    active={rules.includes(r.key)}
                    onToggle={() => toggleRule(r.key)}
                  />
                ))}
                <ChipMenu
                  label="More rules"
                  activeLabel={`More rules: ${
                    rules.filter((r) => !COMMON.some((c) => c.key === r)).length + globals.length
                  }`}
                  name="More rules"
                  options={MORE_OPTIONS}
                  selected={[...rules.filter((r) => !COMMON.some((c) => c.key === r)), ...globals]}
                  multiple
                  onChange={(picked) =>
                    setWanted({
                      ...wanted,
                      rules: [
                        ...rules.filter((r) => COMMON.some((c) => c.key === r)),
                        ...picked.filter((k) => !isGlobal(k)),
                      ],
                      globals: picked.filter(isGlobal),
                    })
                  }
                />
                <ToggleChip
                  label="Surprise me"
                  active={surprise}
                  onToggle={() => setWanted({ ...wanted, surprise: !surprise })}
                />
                {!neverFog(size) && (
                  <ToggleChip
                    label="Fog"
                    active={fog}
                    onToggle={() => setWanted({ ...wanted, fog: !fog })}
                  />
                )}
              </div>
              <p className="mx-1 my-0 text-[12px] text-muted">
                {surprise
                  ? surpriseWords(choice)
                  : plain
                    ? 'No rules: a classic.'
                    : `${capitalized(describe(choice))}, every rule in it.`}
              </p>
              {neverFog(size) && (
                <p className="mx-1 my-0 text-[12px] text-muted">
                  {grid} puzzles don’t come with fog: they take too long to make.
                </p>
              )}
            </div>
            {surprise && (
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-semibold text-muted">How many rules</span>
                <Segmented
                  label="How many rules"
                  value={count === null ? 'any' : String(count)}
                  onChange={(v) => setWanted({ ...wanted, count: v === 'any' ? null : Number(v) })}
                  options={[
                    { value: 'any', label: 'Any' },
                    ...[1, 2, 3, 4, 5].map((n) => ({
                      value: String(n),
                      label: String(n),
                      disabled: n < rules.length || n > maxRules(size),
                    })),
                  ]}
                />
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-muted">Difficulty</span>
              <Segmented
                label="Difficulty"
                value={band}
                onChange={(v: BandKey) => setWanted({ ...wanted, band: v })}
                options={BANDS.map((b) => ({
                  value: b.key,
                  label: b.name,
                  disabled: !reachable.includes(b.key),
                }))}
              />
              <p className="mx-1 my-0 text-[12px] text-muted">
                {reachable.length < BANDS.length && `${whichGrid} goes up to ${hardest}. `}
                {ready ? 'Ready now.' : waitFor(size, fog)}
              </p>
            </div>
            {failed && (
              <p role="alert" className="m-0 text-[14px] text-mistake">
                That didn’t work. Check your connection and try again.
              </p>
            )}
            <button
              type="button"
              disabled={start.isPending}
              onClick={() => start.mutate()}
              className="h-12 rounded-[12px] border-0 bg-accent text-[15px] font-bold text-white disabled:opacity-45"
            >
              Generate
            </button>
          </>
        )}
      </Card>
    </section>
  );
}
